import { useMemo } from "react";

import { Box, Dialog, Link, Paper, Typography } from "@mui/material";

import { useAuthContext } from "@covertree/auth";
import {
  selectGeoCodeResult,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Button } from "@covertree/ui";

import { SALES_EMAIL_ADDRESS } from "@covertree/consts";
import { getFullStateName } from "@utils/getFullStateName";
import { featureFlags } from "@utils/featureFlags";

interface LackOfLicenseOrAppointmentModalProps {
  open: boolean;
  onClose: () => void;
}

export function LackOfLicenseOrAppointmentModal({
  open,
  onClose,
}: LackOfLicenseOrAppointmentModalProps) {
  const newHomeGeoCode = useNewBusinessSelector(selectGeoCodeResult);
  const { basicUserData } = useAuthContext();

  const fullStateName = useMemo(
    () => (newHomeGeoCode?.state ? getFullStateName(newHomeGeoCode.state) : ""),
    [newHomeGeoCode]
  );
  return (
    <Dialog open={open} onClose={onClose}>
      <Paper
        sx={{
          position: "relative",
          marginX: 5,
          marginY: 3,
          textAlign: "center",
          boxShadow: "none",
        }}>
        {featureFlags.useNewAgentIntegration.isEnabled() ? (
          <Typography
            sx={{
              marginX: 3,
              marginY: 2,
            }}>
            There seems to be an issue with your license or the
            license/appointment of your agency in{" "}
            <Typography
              component="span"
              sx={(theme) => ({ color: theme.colors.green3 })}>
              {fullStateName}
            </Typography>
            . If you believe this is an error, please contact us at{" "}
            <Link color="secondary" href={`mailto:${SALES_EMAIL_ADDRESS}`}>
              {SALES_EMAIL_ADDRESS}
            </Link>
            .
          </Typography>
        ) : (
          <Typography
            sx={{
              marginX: 3,
              marginY: 2,
            }}>
            There&rsquo;s something wrong with your, or your agency&rsquo;s,
            license/appointment in{" "}
            <Typography
              component="span"
              sx={(theme) => ({ color: theme.colors.green3 })}>
              {fullStateName}
            </Typography>
            .
            <br />
            If you feel that this is an error, please contact us at{" "}
            {SALES_EMAIL_ADDRESS}.
          </Typography>
        )}
        <Button sx={{ marginX: 3, marginY: 2 }} onClick={onClose}>
          Done
        </Button>
        <Box
          sx={(theme) => ({
            position: "absolute",
            bottom: theme.spacing(-2),
            right: theme.spacing(1),
            display: "flex",
            gap: theme.spacing(1),
            textAlign: "right",
            color: theme.colors.grey1,
          })}>
          <Typography sx={{ fontSize: "0.625rem", lineHeight: 1.4 }}>
            Email: {basicUserData?.email ?? "-"}
          </Typography>
          <Typography sx={{ fontSize: "0.625rem", lineHeight: 1.4 }}>
            User ID: {basicUserData?.sub ?? "-"}
          </Typography>
        </Box>
      </Paper>
    </Dialog>
  );
}
